<template>
  <div class="full-height">
    <div v-if="!isFormCompleted">
      <div v-if="windowWidth > 960" class="mt-4 d-flex justify-center">
        <div
          v-for="(detail, index) in filteredProgressDetails"
          :key="detail.type"
          class="text-center"
        >
          <div class="d-flex align-center">
            <div>
              <v-progress-circular
                :rotate="-90"
                :size="45"
                :width="5"
                :value="
                  index === selectedSection || selectedSection > index ? 100 : 0
                "
                :color="selectedSection > index ? 'green' : 'primary'"
              >
                <v-icon
                  size="20"
                  class="font-weight-bold"
                  :color="selectedSection > index ? 'green' : 'primary'"
                >
                  {{ selectedSection > index ? "check" : detail.icon }}</v-icon
                >
              </v-progress-circular>
              <div
                :class="{ 'font-weight-bold': index === selectedSection }"
                class="body-2 mt-1"
                :style="
                  selectedSection > index
                    ? 'color: green'
                    : 'color: var(--primary-base)'
                "
              >
                {{ detail.heading }}
              </div>
            </div>
            <div
              v-if="detail.type !== 'submit'"
              style="width: 70px; border-top: 2px dotted #b1afaf"
            ></div>
          </div>
        </div>
      </div>
      <VendorBasicDetails
        v-if="currentSectionComponent === 'VendorBasicDetails'"
        :form-list="formList"
        @basic-details-saved="onClickNextSession()"
        @enable-dynamic-form="enableDynamicForm($event)"
      ></VendorBasicDetails>
      <Insurance
        v-else-if="currentSectionComponent === 'Insurance'"
        @back-to-prev="backToPreviousStep()"
        @move-to-next-section="onClickNextSession()"
      ></Insurance>
      <VendorDocuments
        v-else-if="currentSectionComponent === 'VendorDocuments'"
        @back-to-prev="backToPreviousStep()"
        @vendor-documents-saved="onClickNextSession()"
      ></VendorDocuments>
      <VendorBankDetails
        v-else-if="currentSectionComponent === 'VendorBankDetails'"
        @back-to-prev="backToPreviousStep()"
        @bank-saved="onClickNextSession()"
      ></VendorBankDetails>
      <ProjectResources
        v-else-if="currentSectionComponent === 'ProjectResources'"
        @back-to-prev="backToPreviousStep()"
        @move-to-next-section="onClickNextSession()"
      ></ProjectResources>
      <VendorDynamicForm
        v-else-if="currentSectionComponent === 'VendorDynamicForm'"
        :template-id="dynamicFormTemplateId"
        :dynamic-form-required="dynamicFormRequired"
        :template-name="dynamicFormDataTemplateName"
        :form-data="dynamicFormData"
        :response-id="dynamicFormResponseId"
        @back-to-prev="backToPreviousStep()"
        @move-to-next-section="onClickNextSession()"
      ></VendorDynamicForm>
      <FormSubmit
        v-else-if="currentSectionComponent === 'FormSubmit'"
        @back-to-prev="backToPreviousStep()"
        @form-submitted="isFormCompleted = true"
      ></FormSubmit>
    </div>

    <div v-else class="full-height">
      <v-row justify="center" class="full-height">
        <v-col
          cols="12"
          sm="10"
          class="justify-center d-flex align-center"
          :class="isMobileView ? 'flex-column text-center' : ''"
        >
          <v-icon size="100" color="green">check_circle</v-icon>
          <div class="pl-4">
            <div class="text-h4 primary--text">
              You have successfully completed the onboarding
            </div>
            <div class="grey--text text--darken-1 text-h5 mt-3">
              We have sent it for review
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
    <AppLoading v-if="fieldLoading"></AppLoading>
  </div>
</template>

<script>
import VendorBasicDetails from "./VendorBasicDetails.vue";
import ProjectResources from "./ProjectResource.vue";
import Insurance from "./insurance-details/Insurance.vue";
import FormSubmit from "./FormSubmit.vue";
import VendorDocuments from "./documents/VendorDocuments.vue";
import VendorDynamicForm from "./VendorDynamicForm.vue";
import VendorBankDetails from "./VendorBankDetails.vue";

import {
  GET_DYNAMIC_FORM_DETAILS,
  GET_FORM_FIELDS_AND_TYPE,
} from "@/graphql/vendorOnboardQueries";

export default {
  name: "VendorOnboarding",

  components: {
    VendorBasicDetails,
    Insurance,
    ProjectResources,
    FormSubmit,
    VendorDocuments,
    VendorDynamicForm,
    VendorBankDetails,
  },

  data: () => ({
    selectedSection: 0,
    isFormCompleted: false,
    progressDetails: [
      {
        type: "basic",
        progress: 0,
        value: 0,
        icon: "person",
        heading: "Vendor Basic Information",
      },
      {
        type: "insurance",
        progress: 0,
        value: 1,
        icon: "business_center",
        heading: "Insurance",
      },
      {
        type: "documents",
        value: 2,
        icon: "upload_file",
        heading: "Documents",
      },
      {
        type: "bank",
        value: 3,
        icon: "account_balance",
        heading: "Bank Information",
      },
      {
        type: "projectResources",
        progress: 0,
        value: 4,
        icon: "description",
        heading: "Project Resources",
      },
      // {
      //   type: "complianceQuestionnaire",
      //   progress: 0,
      //   value: 3,
      //   icon: "article",
      //   heading: "Compliance Questionnaire",
      // },
      {
        type: "submit",
        value: 5,
        icon: "checklist_rtl",
        heading: "Submit",
      },
    ],
    dynamicFormTemplateId: 0,
    dynamicFormData: [],
    dynamicFormResponseId: 0,
    dynamicFormDataTemplateName: "",
    dynamicFormRequired: 0,
    formList: {},
    fieldLoading: false,
  }),

  computed: {
    // current window size
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    // to check the device is mobile based on window size
    isMobileView() {
      return this.$store.state.isMobileWindowSize;
    },
    // Section visibility based on custom field IDs and Field_Visiblity
    showInsurance() {
      return this.formList[491]?.Field_Visiblity?.toLowerCase() === "yes";
    },
    showDocuments() {
      return this.formList[492]?.Field_Visiblity?.toLowerCase() === "yes";
    },
    showBankDetails() {
      return this.formList[493]?.Field_Visiblity?.toLowerCase() === "yes";
    },
    showProjectResources() {
      return this.formList[494]?.Field_Visiblity?.toLowerCase() === "yes";
    },
    // Current section component name
    currentSectionComponent() {
      return this.getCurrentSectionComponent();
    },
    // Filtered progress details based on visibility
    filteredProgressDetails() {
      let details = [
        {
          type: "basic",
          progress: 0,
          value: 0,
          icon: "person",
          heading: "Vendor Basic Information",
        },
      ];

      let currentValue = 1;

      if (this.showInsurance) {
        details.push({
          type: "insurance",
          progress: 0,
          value: currentValue++,
          icon: "business_center",
          heading: "Insurance",
        });
      }

      if (this.showDocuments) {
        details.push({
          type: "documents",
          value: currentValue++,
          icon: "upload_file",
          heading: "Documents",
        });
      }

      if (this.showBankDetails) {
        details.push({
          type: "bank",
          value: currentValue++,
          icon: "account_balance",
          heading: "Bank Information",
        });
      }

      if (this.showProjectResources) {
        details.push({
          type: "projectResources",
          progress: 0,
          value: currentValue++,
          icon: "description",
          heading: "Project Resources",
        });
      }

      if (this.dynamicFormTemplateId) {
        details.push({
          type: "complianceQuestionnaire",
          progress: 0,
          value: currentValue++,
          icon: "article",
          heading: "Compliance Questionnaire",
        });
      }

      details.push({
        type: "submit",
        value: currentValue,
        icon: "checklist_rtl",
        heading: "Submit",
      });

      return details;
    },
  },

  mounted() {
    this.getFieldProps();
  },

  methods: {
    getFieldProps() {
      let vm = this;
      vm.fieldLoading = true;
      this.$apollo
        .query({
          query: GET_FORM_FIELDS_AND_TYPE,
          variables: {
            form_Id: 231,
          },
          client: "apolloClientB",
          fetchPolicy: "no-cache",
        })
        .then((res) => {
          if (res.data.getFormFeildsByFormIdAndTab) {
            res.data.getFormFeildsByFormIdAndTab.formFields.forEach((list) => {
              this.$set(this.formList, list.Field_Id, list);
            });
          }
          vm.fieldLoading = false;
        })
        .catch(() => {
          vm.fieldLoading = false;
        });
    },
    getCurrentSectionComponent() {
      const currentDetail = this.filteredProgressDetails[this.selectedSection];
      if (!currentDetail) return "FormSubmit";

      switch (currentDetail.type) {
        case "basic":
          return "VendorBasicDetails";
        case "insurance":
          return this.showInsurance ? "Insurance" : null;
        case "documents":
          return this.showDocuments ? "VendorDocuments" : null;
        case "bank":
          return this.showBankDetails ? "VendorBankDetails" : null;
        case "projectResources":
          return this.showProjectResources ? "ProjectResources" : null;
        case "complianceQuestionnaire":
          return this.dynamicFormTemplateId ? "VendorDynamicForm" : null;
        case "submit":
          return "FormSubmit";
        default:
          return "FormSubmit";
      }
    },
    enableDynamicForm(templateId) {
      this.dynamicFormTemplateId = templateId;
      this.getDynamicFormDetails(templateId);
    },
    getDynamicFormDetails(templateId) {
      let vm = this;
      vm.$apollo
        .query({
          query: GET_DYNAMIC_FORM_DETAILS,
          variables: {
            templateId: templateId,
            vendorId: parseInt(localStorage.getItem("vendorId")),
          },
          fetchPolicy: "no-cache",
          client: "apolloClientB",
        })
        .then((data) => {
          data = data.data;
          if (data && data.getDynamicFormDetails) {
            let formData = data.getDynamicFormDetails.dynamicFormDetails[0];
            this.dynamicFormData = JSON.parse(formData.Form_Template);
            if (formData.Response_Id) {
              this.dynamicFormResponseId = formData.Response_Id;
              this.dynamicFormDataTemplateName = formData.Template_Name;
            }
            this.dynamicFormRequired = formData.Required
              ? formData.Required
              : 0;
          }
        })
        .catch((err) => {
          let snackbarData = {
            isOpen: true,
            message:
              err.graphQLErrors.length !== 0
                ? err.graphQLErrors[0].message
                : "Something went wrong. It could be a possibility due to network connectivity error..",
            type: "warning",
          };
          vm.showAlert(snackbarData);
        });
    },
    onClickNextSession() {
      const nextIndex = this.selectedSection + 1;
      if (nextIndex < this.filteredProgressDetails.length) {
        this.selectedSection = nextIndex;
      }
    },
    backToPreviousStep() {
      const prevIndex = this.selectedSection - 1;
      if (prevIndex >= 0) {
        this.selectedSection = prevIndex;
      }
    },
    // show error or success message
    showAlert(snackbarData) {
      this.$store.commit("OPEN_SNACKBAR", snackbarData);
    },
  },
};
</script>
